<template>
  <v-card
    class="mx-auto"
    color="transparent"
    elevation="0"
  >
    <div ref="scrollTarget"></div>

    <v-card
      :style="`z-index: 10; position: sticky; top: ${focusModeToolbarTop()};`"
      color="rgba(255, 255, 255, 0.8)"
    >
      <v-toolbar
        flat
        color="transparent"
      >
        <v-btn small icon fab class="px-0" text @click="$router.back()">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-skeleton-loader
          :loading="!collaborator.hasOwnProperty('uuid')"
          type="text"
          min-width="200px"
          class="pl-4"
        >
          <v-toolbar-title>Candidate <strong>{{ collaboratorName(collaborator) }}</strong></v-toolbar-title>
        </v-skeleton-loader>
        <v-spacer />
        <section
          class="d-flex align-center"
          style="gap: 32px;"
        >
          <CandidateAssignOffer
            :item="collaborator"
            @handle-loading="(value) => handleLoading(value)"
          />
        </section>
      </v-toolbar>
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
      />
    </v-card>

    <section
      class="
        md-and-up-sticky
        d-flex flex-column flex-md-row
        px-4 py-1 py-md-0
      "
      :class="{ 'mt-6 mt-md-0' : focusMode }"
      :style="`z-index: 10; background-color: rgba(255, 255, 255, 0.8); gap: 16px; top: ${focusModeToolbarTop('tabs')};`"
    >
      <v-tabs
        v-model="tabs.activeTab"
        centered
        background-color="rgba(255, 255, 255, 0.8)"
        :style="`position: sticky; top: ${$store.state.focusMode ? '88px': '152px'};`"
        show-arrows
        @change="changedTab"
      >
        <v-tab
          href="#candidate"
          :disabled="!canPerformAction('VIEW_PROSPECT') || !collaborator?.uuid"
        >
          CANDIDAT
        </v-tab>
        <v-tab
          href="#center-applications"
          :disabled="!canPerformAction('VIEW_APPLICATION') || !collaborator?.uuid"
        >
          CANDIDATURES DU CENTRE
        </v-tab>
        <v-tab
          href="#other-applications"
          :disabled="!canPerformAction('VIEW_APPLICATION') || !collaborator?.uuid"
        >
          AUTRES CANDIDATURES
        </v-tab>
      </v-tabs>
    </section>

    <section
      class="
        d-md-flex justify-space-between
        my-8 px-4 px-md-8
      "
      :class="{ 'mt-12' : focusMode }"
    >
      <div
        class="col-12 pa-0"
      >
        <v-card-text
          class="
            d-flex flex-column
            pa-0
          "
          style="gap: 26px;"
        >
          <v-expansion-panels
            v-show="tabs.activeTab === 'candidate'"
            v-model="tabs.panels.candidate"
            multiple
            flat
          >
            <Application
              :fields="forms.personalInformation.fields"
              title="Coordonnées"
              :application="collaborator"
              :is-loading="isLoading"
              permission="PROSPECT"
              @is-edit-mode="(value) => changeOpenPanels('candidate', value, 0)"
              @save-changes="(data) => updateProspect({ uuid: collaborator.uuid, ...data, ...input })"
            />

            <Competence
              :fields="forms.experiences.fields"
              title="Expériences professionelles"
              :items="collaborator.experiences"
              permission="PROSPECT"
              @is-edit-mode="(value) => changeOpenPanels('candidate', value, 1)"
              @save-changes="(competenceIndex, data, isDelete) => competencePopup('experiences', competenceIndex, data, isDelete)"
            />
            <Competence
              :fields="forms.formations.fields"
              title="Formations"
              :items="collaborator.formations"
              permission="PROSPECT"
              @is-edit-mode="(value) => changeOpenPanels('candidate', value, 2)"
              @save-changes="(competenceIndex, data, isDelete) => competencePopup('formations', competenceIndex, data, isDelete)"
            />

            <Document
              title="Tous les documents"
              :collaborator-uuid="collaborator.uuid"
              :items="collaborator.attachments"
              :is-loading="isLoading"
              permission="PROSPECT"
              @is-edit-mode="(value) => changeOpenPanels('candidate', value, 3)"
              @attachment-uploaded="(attachments) => setAttachments(attachments)"
              @attachment-delete="(attachmentIndex) => deleteAttachment(attachmentIndex)"
              @attachment-edit="(attachments) => editAttachments(attachments)"
            />

            <Competence
              title="Langues"
              :fields="forms.languages.fields"
              :items="collaborator.languages"
              permission="PROSPECT"
              @is-edit-mode="(value) => changeOpenPanels('candidate', value, 4)"
              @save-changes="(competenceIndex, data, isDelete) => competencePopup('languages', competenceIndex, data, isDelete)"
            />
          </v-expansion-panels>

          <v-container
            v-if="tabs.activeTab === 'center-applications'"
            class="col-12 pa-0"
          >
            <v-skeleton-loader
              :loading="isLoading"
              :type="`list-item@10`"
            >
              <ListApplications
                variant="center"
                :applications="applications.center"
              />
            </v-skeleton-loader>
          </v-container>

          <v-container
            v-if="tabs.activeTab === 'other-applications'"
            class="col-12 pa-0"
          >
            <v-skeleton-loader
              :loading="isLoading"
              :type="`list-item@10`"
            >
              <ListApplications
                variant="other"
                :applications="applications.other"
              />
            </v-skeleton-loader>
          </v-container>
        </v-card-text>
      </div>
    </section>
  </v-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import moment from 'moment';

import { requests } from '@/mixins/index';

import Competence from '@/components/application/Competence.vue';
import Application from '@/components/application/Application.vue';
import Document from '@/components/dynamic/form/components/Document.vue';
import CandidateAssignOffer from '@/components/application/components/CandidateAssignOffer.vue';
import ListApplications from '@/components/application/components/ListApplications.vue';

export default {
  name: 'Candidate',
  components: {
    Application,
    Competence,
    Document,
    CandidateAssignOffer,
    ListApplications
  },
  data () {
    return {
      candidateUuid: this.$route.params.candidateUuid,
      collaborator: {},
      application: {},
      isLoading: false,
      editList: [],
      forms: {
        personalInformation: {
          fields: [
            {
              name: 'firstname',
              label: 'Prénom',
              computed: 'firstname',
              viewType: 'Text',
              icon: 'account',
              type: 'text',
              required: true,
              mandatory: true
            },
            {
              name: 'lastname',
              label: 'Nom',
              computed: 'lastname',
              viewType: 'Text',
              icon: 'account',
              type: 'text',
              required: true,
              mandatory: true
            },
            {
              name: 'birth_date',
              label: 'Date de naissance',
              computed: 'birth_date',
              component: 'DatePicker',
              icon: 'calendar',
              type: 'date',
              min: '1920-01-01',
              required: true,
              mandatory: true
            },
            {
              name: 'mobilephone',
              label: 'Mobile',
              computed: 'mobilephone',
              component: 'MobilePhoneInput',
              icon: 'phone',
              type: 'phone',
              required: true,
              mandatory: true
            },
            {
              name: 'email',
              label: 'E-mail',
              computed: 'email',
              viewType: 'Text',
              icon: 'email',
              type: 'text',
              required: true,
              mandatory: true,
              rules: [
                value => {
                  const emailRegex = /^(?=[^\s@]{0,64}@)[^\s@]+@[^\s@]+\.[^\s@.]+$/;
                  return emailRegex.test(value) || 'Format d\'email invalide';
                }
              ]
            },
            {
              name: 'employeeNumber',
              label: 'Matricule',
              computed: 'employeeNumber',
              viewType: 'Text',
              icon: 'card-account-details',
              type: 'text',
              disabled: true,
              readonly: true
            }
          ]
        },
        experiences: {
          popup: {
            add: {
              title: 'Sauvegarder - Expérience professionnelle',
              text: 'Souhaitez-vous confirmer l’enregistrement ?'
            },
            edit: {
              title: 'Mise à jour - Expérience professionnelle',
              text: 'Souhaitez-vous confirmer la mise à jour de l\'enregistrement ?'
            },
            delete: {
              title: 'Suppression - Expérience professionnelle',
              text: 'Souhaitez-vous confirmer la suppression de l’enregistrement ?'
            }
          },
          fields: [
            {
              name: 'title',
              label: 'Intitulé de poste',
              viewType: 'Text',
              icon: 'format-title',
              type: 'text',
              required: true,
              mandatory: true
            },
            {
              name: 'location',
              label: 'Lieu',
              viewType: 'Text',
              icon: 'map-marker',
              type: 'text'
            },
            {
              name: 'contractType',
              label: 'Type de contrat',
              viewType: 'Select',
              icon: 'file-sign',
              clearable: true,
              optionsByReference: 'contract_type'
            },
            {
              name: 'startDate',
              label: 'Date de début',
              component: 'DatePicker',
              icon: 'calendar',
              type: 'date',
              max: 'endDate'
            },
            {
              name: 'endDate',
              label: 'Date de fin',
              component: 'DatePicker',
              icon: 'calendar',
              type: 'date',
              min: 'startDate'
            },
            {
              name: 'description',
              label: 'Description',
              viewType: 'Textarea',
              icon: 'text-long',
              type: 'text',
              fullWidth: true
            }
          ]
        },
        formations: {
          popup: {
            add: {
              title: 'Sauvegarder - Formation',
              text: 'Souhaitez-vous confirmer l’enregistrement ?'
            },
            edit: {
              title: 'Mise à jour - Formation',
              text: 'Souhaitez-vous confirmer la mise à jour de l\'enregistrement ?'
            },
            delete: {
              title: 'Suppression - Formation',
              text: 'Souhaitez-vous confirmer la suppression de l’enregistrement ?'
            }
          },
          fields: [
            {
              name: 'qualificationUuid',
              label: 'Diplômes et qualifications',
              viewType: 'Select',
              icon: 'certificate',
              component: 'PositionInput',
              required: true,
              mandatory: true,
              contentWtype: 'qualification',
              computedLabel: 'qualification.name'
            },
            {
              name: 'title',
              label: 'Domaine d\'études',
              viewType: 'Text',
              icon: 'briefcase-outline',
              type: 'text',
              required: true,
              mandatory: true
            },
            {
              name: 'institution',
              label: 'École',
              viewType: 'Text',
              icon: 'school-outline',
              type: 'text',
              required: true,
              mandatory: true
            },
            {
              name: 'startDate',
              label: 'Date de début',
              component: 'DatePicker',
              icon: 'calendar',
              type: 'date',
              max: 'endDate'
            },
            {
              name: 'endDate',
              label: 'Date de fin',
              component: 'DatePicker',
              icon: 'calendar',
              type: 'date',
              min: 'startDate'
            }
          ]
        },
        languages: {
          popup: {
            add: {
              title: 'Sauvegarder - Langue',
              text: 'Souhaitez-vous confirmer l’enregistrement ?'
            },
            edit: {
              title: 'Mise à jour - Langue',
              text: 'Souhaitez-vous confirmer la mise à jour de l\'enregistrement ?'
            },
            delete: {
              title: 'Suppression - Langue',
              text: 'Souhaitez-vous confirmer la suppression de l’enregistrement ?'
            }
          },
          fields: [
            {
              name: 'language',
              label: 'Sélectionner',
              viewType: 'Select',
              icon: 'translate',
              type: 'text',
              required: true,
              mandatory: true,
              clearable: true,
              optionsByReference: 'languages'
            },
            {
              name: 'level',
              label: 'Niveau',
              viewType: 'Select',
              icon: 'ab-testing',
              type: 'text',
              required: true,
              mandatory: true,
              clearable: true,
              optionsByReference: 'levels'
            }
          ]
        }
      },
      input: {},
      mobilePhoneIsValid: true,
      hideSidebar: false,
      tabs: {
        activeTab: 'candidate',
        panels: { candidate: [0, 1, 2, 3, 4] }
      },
      applications: {
        center: [],
        other: []
      }
    };
  },
  computed: {
    ...mapState(['references', 'user', 'activeWorkspace', 'focusMode']),
    ...mapGetters(['getActiveWorkspaceUuid', 'canPerformAction']),
    collaboratorName () {
      return collaborator => `${collaborator.firstname} ${collaborator.lastname}`;
    },
    formatedDate () {
      return date => {
        if (!date) return '';

        return moment(date).format('DD-MM-YYYY');
      };
    },
    focusModeToolbarTop () {
      return (section = null) => {
        if (this.$vuetify.breakpoint.xsOnly) {
          return '64px';
        }

        if (section === 'tabs') {
          return this.focusMode ? '88px' : '150px';
        }

        return this.focusMode ? '24px' : '88px';
      };
    }
  },
  mounted () {
    this.scrollTo('scrollTarget');

    this.fetchData();
  },
  methods: {
    scrollTo (field) {
      this.$vuetify.goTo(this.$refs[field], {
        duration: 500,
        offset: 64 + 16,
        easing: 'easeInOutCubic'
      });
    },
    handleLoading (value) {
      this.isLoading = value;
    },
    changeOpenPanels (panel, isOpen, panelIndex) {
      if (isOpen) {
        if (!this.tabs.panels[panel].includes(panelIndex)) {
          this.tabs.panels[panel].push(panelIndex);
        }
      } else {
        this.tabs.panels[panel] = this.tabs.panels[panel].filter(pan => pan !== panelIndex);
      }
    },
    handleValidation (vueTelPhoneObject) {
      this.mobilePhoneIsValid = vueTelPhoneObject.valid;
    },
    removeFields (input) {
      delete input.collaborator;
      delete input.recruiter;
      delete input.offer;
    },
    async fetchData () {
      this.handleLoading(true);

      const { data, success } = await requests.getProspect(this.candidateUuid);

      if (success) {
        if (!data) {
          this.$router.push({ name: 'error', params: { errorCode: 404 } });
          return;
        }

        this.collaborator = data;
      }

      this.handleLoading(false);
    },
    handleChanges (form, field, value) {
      if (form === 'collaborator') {
        if (field.name === 'mobilephone') {
          value = this.mobilePhoneIsValid ? value : null;
        }
      }

      this.input[field.name] = value;
    },
    async updateProspect (input) {
      if (!this.canPerformAction('EDIT_PROSPECT')) return;

      this.handleLoading(true);

      const inputCopyForClick = { ...input };
      this.removeFields(inputCopyForClick);
      const response = await requests.updateProspect({ input: inputCopyForClick, isEditMode: true });

      if (!response.success) {
        this.$store.commit('setSnackbar', {
          text: 'Erreur lors de l\'enregistrement.',
          color: 'error'
        });
      } else {
        this.collaborator = response.data;

        this.$store.commit('setSnackbar', {
          text: 'Enregistrer avec succès.',
          color: 'success'
        });
      }

      this.handleLoading(false);
    },
    setAttachments (attachments) {
      const attachmentsAux = [];

      if (this.collaborator.attachments) {
        attachmentsAux.push(...this.collaborator.attachments);
      }

      attachmentsAux.push(...attachments);

      this.updateProspect({ uuid: this.collaborator.uuid, attachments: attachmentsAux });
    },
    editAttachments (attachments) {
      this.updateProspect({ uuid: this.collaborator.uuid, attachments: attachments });
    },
    deleteAttachment (attachmentIndex) {
      const attachmentsAux = Array.isArray(this.collaborator.attachments)
        ? [...this.collaborator.attachments]
        : [];

      attachmentsAux.splice(attachmentIndex, 1);

      this.updateProspect({
        uuid: this.collaborator.uuid,
        attachments: attachmentsAux
      });
    },
    competencePopup (competenceType, competenceIndex, data, isDelete) {
      let popupType;

      if (isDelete) popupType = 'delete';
      else if (!competenceIndex) popupType = 'add';
      else popupType = 'edit';

      this.$store.commit('setDialog', {
        show: true,
        ...this.forms[competenceType].popup[popupType],
        handleAccept: checked => this.updateCompetence(competenceType, competenceIndex, data, checked, isDelete)
      });
    },
    generateUniqueId () {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 15);
      const uniqueId = `${timestamp}_${random}`;
      return uniqueId;
    },
    updateCompetence (competenceType, competenceIndex, data, checked, isDelete) {
      // Get copy of competences array
      const competences = [...(this.collaborator[competenceType] || [])];

      if (isDelete) {
        competences.splice(competenceIndex, 1);
      } else if (![null, undefined].includes(competenceIndex)) {
        competences[competenceIndex] = {
          ...competences[competenceIndex],
          ...data
        };
      } else {
        competences.unshift({
          ...data,
          id: `${competenceType}_${this.generateUniqueId()}`
        });
      }

      // Sort based on type
      if (competenceType === 'experiences') {
        competences.sort(this.sortExperience);
      } else if (competenceType === 'languages') {
        competences.sort(this.sortLanguages);
      }

      // Remove nested qualification object from formations before saving
      const cleanedCompetences = competenceType === 'formations'
        ? competences.map(({ qualification, ...rest }) => rest)
        : competences;

      this.updateProspect({
        uuid: this.collaborator.uuid,
        [competenceType]: cleanedCompetences
      });
    },
    sortExperience (a, b) {
      // Convert dates to timestamps for reliable comparison
      const aStart = a.startDate ? new Date(a.startDate).getTime() : 0;
      const bStart = b.startDate ? new Date(b.startDate).getTime() : 0;
      const aEnd = a.endDate ? new Date(a.endDate).getTime() : 0;
      const bEnd = b.endDate ? new Date(b.endDate).getTime() : 0;

      // Handle cases where dates are missing
      if (!aStart && bStart) return 1;
      if (aStart && !bStart) return -1;

      // Sort by start date descending (most recent first)
      if (aStart !== bStart) {
        return bStart - aStart;
      }

      // If start dates are equal, sort by end date descending
      return bEnd - aEnd;
    },
    sortLanguages (a, b) {
      const aWeight = this.references.levelsWeight[a.level] || Number.MAX_SAFE_INTEGER;
      const bWeight = this.references.levelsWeight[b.level] || Number.MAX_SAFE_INTEGER;

      return aWeight - bWeight;
    },
    fetchApplications (filterByWorkspace = false) {
      this.handleLoading(true);

      requests.getCollaboratorApplications({
        start: 0,
        limit: 1000,
        query: {
          bool: {
            filter: [
              { term: { wtype: 'application' } },
              { term: { 'collaborator.uuid': this.collaborator.uuid } },
              {
                bool: {
                  must_not: [
                    { term: { canceled: true } }
                  ]
                }
              },
              (filterByWorkspace === true
                ? { bool: { must: [{ term: { 'entity.uuid': this.getActiveWorkspaceUuid } }] } }
                : { bool: { must_not: [{ term: { 'entity.uuid': this.getActiveWorkspaceUuid } }] } }
              )
            ]
          }
        },
        sort: [{ lastUpdateTime: { order: 'desc' } }]
      }).then(response => {
        if (response.success) {
          const applicationsKey = filterByWorkspace ? 'center' : 'other';
          this.applications[applicationsKey] = response.data;
        }
      }).finally(() => this.handleLoading(false));
    },
    changedTab (tab) {
      this.scrollTo('scrollTarget');

      if (tab === 'center-applications' && !this.applications.center.length) {
        this.fetchApplications(true);
      }
      if (tab === 'other-applications' && !this.applications.other.length) {
        this.fetchApplications();
      }
    }
  }
};
</script>

<style>
.v-list-item__subtitle,
.v-list-item__title {
  white-space: break-spaces;
}

.switch-flex-column.v-input--switch .v-input__slot {
  display: flex;
  flex-direction: column;
  align-items: start;
}

.vue-tel-input.vue-tel-input {
  border-top: 0;
  border-right: 0;
  border-left: 0;

  &:focus-within {
    box-shadow: none;
    border-color: #c71900;
  }
}
</style>
